import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tooltip as MuiTooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Button,
  TextField,
} from '@mui/material';

type Placement = 'top' | 'right' | 'bottom' | 'left';
type Arrow = boolean;

const TooltipShowcase = () => {
  // Properties
  const [placement, setPlacement] = useState<Placement>('top');
  const [arrow, setArrow] = useState<Arrow>(false);
  const [tooltipText, setTooltipText] = useState('Tooltip text');
  const [enterDelay, setEnterDelay] = useState(0);
  const [leaveDelay, setLeaveDelay] = useState(0);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Tooltip Component
      </Typography>
      <Typography variant="body1" paragraph>
        The Tooltip component displays informative text when users hover over, focus on, or tap an element.
      </Typography>

      {/* Preview */}
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Preview
        </Typography>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 200,
            width: '100%',
          }}>
          <MuiTooltip
            title={tooltipText}
            placement={placement}
            arrow={arrow}
            enterDelay={enterDelay}
            leaveDelay={leaveDelay}>
            <Button variant="contained">Hover me!</Button>
          </MuiTooltip>
        </Box>
      </Paper>

      {/* Controls */}
      <Paper elevation={1} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Properties
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Placement</InputLabel>
            <Select value={placement} label="Placement" onChange={(e) => setPlacement(e.target.value as Placement)}>
              <MenuItem value="top">Top</MenuItem>
              <MenuItem value="right">Right</MenuItem>
              <MenuItem value="bottom">Bottom</MenuItem>
              <MenuItem value="left">Left</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            size="small"
            label="Tooltip Text"
            value={tooltipText}
            onChange={(e) => setTooltipText(e.target.value)}
          />

          <TextField
            fullWidth
            size="small"
            type="number"
            label="Enter Delay (ms)"
            value={enterDelay}
            onChange={(e) => setEnterDelay(Number(e.target.value))}
          />

          <TextField
            fullWidth
            size="small"
            type="number"
            label="Leave Delay (ms)"
            value={leaveDelay}
            onChange={(e) => setLeaveDelay(Number(e.target.value))}
          />
        </Box>

        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={<Switch checked={arrow} onChange={(e) => setArrow(e.target.checked)} />}
            label="Show Arrow"
          />
        </Box>

        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Usage Example:
          </Typography>
          <Typography
            variant="body2"
            component="pre"
            sx={{
              backgroundColor: 'grey.100',
              p: 2,
              borderRadius: 1,
              overflow: 'auto',
            }}>
            {`<Tooltip
  title="${tooltipText}"
  placement="${placement}"
  arrow={${arrow}}
  enterDelay={${enterDelay}}
  leaveDelay={${leaveDelay}}
>
  <Button variant="contained">Hover me!</Button>
</Tooltip>`}
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default TooltipShowcase;
